import { notification } from 'antd'
import i18next from 'i18next'
import { action, computed, makeObservable, observable } from 'mobx'

import { setLocalStorageData, STORAGE_KEY } from 'services/localStorage.service'
import { TimeWaiter } from 'services/TimeWaiter'
import { IRootStore } from 'store/types'
import { isGeDomain } from 'utils/helpers/domains/getDomain'
import { ALL_EXCEPT_NUMBERS_REGEXP } from 'shared/constants/regExp'
import { Country } from 'shared/types/types'

import { userApi } from '../api'
import { CountryItem, DEVICE_TYPE, UserModuleKeys } from '../types'

const getKey = (key: string) => `${UserModuleKeys.api}.${key}`

export enum LoginStatus {
  /** получили токен при входе (еще не токен для входа в приложение) */
  loggedIn = 0,
  /** не успешный вход */
  notLogged = 1,
  /** получили токен авторизации (токен для входа в приложение) */
  validateCode = 3,
}

export class LoginUiStore {
  constructor(private readonly rootStore: IRootStore) {
    makeObservable(this)
    this.setDefaultState()
  }

  @observable phonePrefix: string = '+7'
  @observable countryCode: string = '7'
  @observable phone: string = ''
  @observable code: string = ''
  @observable codeLength: number = 0
  @observable loading: boolean = false
  timeWaiter = new TimeWaiter()

  // Сделано как временное решение пока нет дизайна
  @observable isValidateVisible: boolean = false

  /** токен, который получаем после api/v2/login */
  private loginToken: string = ''
  private timerId: NodeJS.Timeout | null = null

  get appStore() {
    return this.rootStore.appStore
  }

  get country() {
    return this.rootStore.appStore.selectedCountry
  }

  @computed
  get fullPhone() {
    return `${this.phonePrefix}${this.phone}`
  }

  @computed
  get maxPhoneLengthByCountry() {
    let result = 0

    if ([Country.BY, Country.UZ, Country.KG, Country.GE, Country.TJ].includes(this.country)) {
      result = 9
    } else if ([Country.RU, Country.KZ].includes(this.country)) {
      result = 10
    } else {
      result = 8
    }

    return result
  }

  @computed
  get phoneMaskByCountry() {
    let mask = '000 000-00-00'

    if ([Country.BY, Country.UZ, Country.KG].includes(this.country)) {
      mask = '00-000-00-00'
    } else if ([Country.GE, Country.TJ].includes(this.country)) {
      mask = '00-000-0000'
    } else if ([Country.AM].includes(this.country)) {
      mask = '00-000000'
    }

    return mask
  }

  @computed
  get allowLogin() {
    return this.maxPhoneLengthByCountry === this.phone.length
  }

  @action
  setValidateVisible = (value: boolean) => {
    this.isValidateVisible = value
  }

  @action
  setPhonePrefix = (value: string) => {
    this.phonePrefix = value
  }

  @action
  setCountryCode = (value: string) => {
    this.countryCode = value
  }

  @action
  setPhone = (value: string) => {
    this.phone = value.replace(ALL_EXCEPT_NUMBERS_REGEXP, '')
  }

  @action
  setCode = (value: string) => {
    this.code = value
  }

  @action
  setCodeLength = (value: number) => {
    this.codeLength = value
  }

  @action
  setLoading = (value: boolean) => {
    this.loading = value
  }

  @action
  setSelectedCountry = (value?: CountryItem) => {
    if (!value) return
    this.rootStore.appStore.setSelectedCountry(value.country)
    this.phonePrefix = value.value
    this.setCountryCode(value.value)
  }

  @action
  tryLogin = async (resend: boolean) => {
    if (this.loading) {
      return LoginStatus.notLogged
    }

    try {
      this.setLoading(true)
      this.timeWaiter.clearTimer() // если таймер запущен, то останавливаем его, чтобы не запускать несколько таймеров

      const response = await userApi.loginByPhone({
        phone: +`${this.phone}`,
        resend,
        deviceType: DEVICE_TYPE.DEVICE_WEB,
        deviceUuid: await this.getDeviceUuid(),
        country: this.rootStore.appStore.selectedCountry,
      })

      const { data, code_length, code } = response

      this.setLoginToken(data)
      this.setCodeLength(code_length)

      if (code) {
        this.setLoading(false)
        this.setCode(code)
        const result = await this.validateCode()
        if (result) {
          return LoginStatus.validateCode
        }
      }
      this.timeWaiter.startTimer()
      return LoginStatus.loggedIn
    } catch (error) {
      notification.open({
        message: i18next.t(getKey('tryLogin')),
        description: (error as Error).message,
        duration: 3,
      })
      return LoginStatus.notLogged
    } finally {
      this.setLoading(false)
    }
  }

  validateCode = async () => {
    if (this.loading || this.code.length !== this.codeLength) {
      return
    }

    try {
      this.setLoading(true)
      const response = await userApi.sendSmsCode({
        token: this.loginToken,
        code: this.code,
        // TODO узнать за deviceToken возможно из-за него неправильное сообщение
        // При блокировке на 5 мин
        // deviceToken: this.rootStore.appStore.deviceToken,
        deviceType: DEVICE_TYPE.DEVICE_WEB,
        deviceUuid: await this.getDeviceUuid(),
        alianDevice: false,
        appVersion: 'v20.00.00',
        deviceName: 'mobile',
      })

      setLocalStorageData(STORAGE_KEY.ACCESS_TOKEN, response.access.token)
      setLocalStorageData(STORAGE_KEY.REFRESH_TOKEN, response.refresh.token)
      this.clearTimer()
      return true
    } catch (error) {
      notification.error({
        message: i18next.t(getKey('tryLogin')),
        description: (error as Error).message,
      })
      return false
    } finally {
      this.setLoading(false)
    }
  }

  clearTimer = () => {
    if (this.timerId) {
      clearInterval(this.timerId)
    }
  }

  useAuthHash = async (authHash: string) => {
    try {
      const response = await userApi.useAuthHash(authHash)

      setLocalStorageData(STORAGE_KEY.ACCESS_TOKEN, response.access.token)
      setLocalStorageData(STORAGE_KEY.REFRESH_TOKEN, response.refresh.token)

      return true
    } catch (error) {
      notification.error({
        message: i18next.t(getKey('tryLogin')),
        description: (error as Error).message,
      })
    }
  }

  @action
  setDefaultState = () => {
    if (isGeDomain()) {
      this.rootStore.appStore.setSelectedCountry(Country.GE)
      this.countryCode = '995'
      this.phonePrefix = '+995'
    } else {
      this.rootStore.appStore.setSelectedCountry(Country.RU)
      this.countryCode = '7'
      this.phonePrefix = '+7'
    }

    this.phone = ''
    this.code = ''
    this.loading = false
    this.timeWaiter = new TimeWaiter()
    this.loginToken = ''
    this.timerId = null
    this.isValidateVisible = false
  }

  @action
  private setLoginToken = (value: string) => {
    this.loginToken = value
  }

  private async getDeviceUuid() {
    return this.rootStore.appStore.getDeviceUuid()
  }
}
